<script>
import axios from '@/utils/axios';
import { Modal } from 'bootstrap';

// items search ka endpoint (agar backend ka alag dropdown endpoint ho, jaise '/items/dropdown', to bas yahan badal do)
const ITEM_SEARCH_URL = '/items';

const blankLine = () => ({
  id: null, // existing line ka id (edit me); backend lines ko id se sync karta hai
  type: 'group', // 'group' => child_item_group_id, 'item' => child_item_id
  child_item_group_id: '',
  child_item_id: '',
  qty_multiplier: 1,
  rmt_width_multiplier: '',
  rmt_height_multiplier: '',
  option_set: '',
});

const blankForm = () => ({
  id: null,
  name: '',
  main_item_id: '',
  lines: [blankLine()],
});

// API kabhi paginated {data:{data:[]}} deti hai kabhi plain {data:[]}
const toList = (res) => {
  const d = res?.data?.data;
  return Array.isArray(d) ? d : (d?.data || []);
};

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: 'CRM',
      text: 'CRM',
      tableColumns: [
        { key: 'checkbox', label: '', width: '40px' },
        { key: 'name', label: 'Name' },
        { key: 'main_item', label: 'Main Item' },
        { key: 'lines_count', label: 'Lines' },
        { key: 'created_at', label: 'Created At' },
        { key: 'actions', label: 'Actions' },
      ],
      data: [],
      pagination: { current_page: 1, per_page: 25, last_page: 1, total: 0, links: [] },
      search_keyword: '',
      selected_rows: [],

      items: [], // vue-select ke options = sirf search result (production.vue ke leads jaisa)
      itemCache: {}, // id => item (selected values ka label dikhane ke liye)
      itemsLoading: false,
      itemSearchTimer: null,
      item_groups: [],

      form: blankForm(),
      errors: {},
      saving: false,

      cloneRow: null,
      clone_main_item_id: '',

      resolveForm: { item_id: '', width: '', height: '', qty: '' },
      resolved: false, // resolve call ho chuki hai (null result = mapping nahi mili)
      resolveResult: null,
      resolveError: null,
    };
  },

  computed: {
    text1() {
      return `Item Mapping (${this.pagination.total})`;
    },
    allSelected() {
      return this.data?.length && this.selected_rows.length === this.data.length;
    },
    isEdit() {
      return !!this.form.id;
    },
    itemOptions() {
      // search result + abhi selected items (taaki selected ka label hamesha dikhe)
      const selected = new Set(
        [
          this.form.main_item_id,
          this.clone_main_item_id,
          this.resolveForm.item_id,
          ...this.form.lines.map((l) => l.child_item_id),
        ].filter(Boolean),
      );
      const map = new Map();
      this.items.forEach((i) => map.set(i.id, i));
      selected.forEach((id) => {
        if (!map.has(id) && this.itemCache[id]) map.set(id, this.itemCache[id]);
      });
      return [...map.values()].map((i) => ({ id: i.id, label: i.code ? `${i.code} — ${i.name}` : i.name }));
    },
    groupOptions() {
      return this.item_groups.map((g) => ({ id: g.id, label: g.name }));
    },
    // resolve response ka chhota summary (keys: common / option_sets, shape dono tarah handle)
    resolveSummary() {
      const r = this.resolveResult;
      if (!r) return null;
      const common = r.common ?? r.common_rows ?? [];
      const sets = r.option_sets ?? [];
      const setNames = Array.isArray(sets)
        ? sets.map((s) => s?.name ?? s?.option_set ?? '').filter(Boolean)
        : Object.keys(sets);
      return { common: common.length, sets: setNames };
    },
    // option_set ke suggestions: isi form ki lines mein jo naam pehle se likhe hain
    optionSetSuggestions() {
      const set = new Set(this.form.lines.map((l) => (l.option_set || '').trim()).filter(Boolean));
      return [...set];
    },
    lineTypeOptions() {
      return [
        { id: 'group', label: 'Category' },
        { id: 'item', label: 'Item' },
      ];
    },
    // un-mapped keys (jaise `id`, `lines`) jo kisi field ke neeche nahi dikhte, unka alert
    errorsOther() {
      const out = [];
      Object.entries(this.errors || {}).forEach(([key, val]) => {
        if (['name', 'main_item_id', 'item_id', 'width', 'height'].includes(key)) return;
        if (key.startsWith('lines.')) return;
        out.push(Array.isArray(val) ? val[0] : val);
      });
      return out;
    },
  },

  async mounted() {
    this.itemMappings();
    this.loadDropdowns();
  },

  methods: {
    // ---------- helpers ----------
    showModal(id) {
      const el = document.getElementById(id);
      let modal = Modal.getInstance(el);
      if (!modal) modal = new Modal(el);
      modal.show();
    },
    hideModal(id) {
      const modal = Modal.getInstance(document.getElementById(id));
      if (modal) modal.hide();
    },
    lineError(i, field) {
      return this.fieldError(`lines.${i}.${field}`);
    },
    fieldError(key) {
      const e = this.errors?.[key];
      return Array.isArray(e) ? e[0] : e;
    },
    // backend ke field errors store karta hai + toast ke liye ek readable message return karta hai
    // interceptor kisi bhi shape mein error reject kare, backend payload yahan se nikal lete hain
    errorPayload(error) {
      return error?.response?.data ?? error?.data ?? error;
    },
    readErrors(error) {
      console.log('API error:', error); // TEMP: shape check ke baad hata dena
      const errs = this.errorPayload(error)?.errors;
      this.errors = errs && typeof errs === 'object' ? errs : {};
      return this.firstError(error);
    },
    firstError(error) {
      const data = this.errorPayload(error);
      const errs = data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs)[0];
        return Array.isArray(first) ? first[0] : first;
      }
      return data?.message || error?.message || 'Something went wrong';
    },

    // ---------- list ----------
    async itemMappings(page = 1) {
      this.setLoading(true);
      try {
        const params = { page, search_keyword: this.search_keyword.trim() || '' };
        const query_string = new URLSearchParams(params).toString();
        const response = await axios.get(`/item-mappings?${query_string}`);
        const res = response.data.data;
        this.data = res.data;
        this.pagination = {
          current_page: res.current_page,
          per_page: res.per_page,
          last_page: res.last_page,
          total: res.total,
          links: res.links,
        };
        this.selected_rows = [];
      } catch (error) {
        this.$swalToast(this.firstError(error), 'error');
      } finally {
        this.setLoading(false);
      }
    },

    clearFilter() {
      this.search_keyword = '';
      this.itemMappings();
    },

    handleToggleAll(event) {
      this.selected_rows = this.$toggleAll(event, this.data);
    },

    async loadDropdowns() {
      try {
        // item-groups poori list ek hi baar mein deta hai (pagination nahi)
        const groupsRes = await axios.get('/item-groups');
        this.item_groups = toList(groupsRes);
      } catch (error) {
        this.$swalToast(this.firstError(error), 'error');
      }
    },

    // items ka server-side search (production.vue ke lead dropdown jaisa)
    async fetchItems(search) {
      this.itemsLoading = true;
      try {
        const res = await axios.get(ITEM_SEARCH_URL, {
          params: { search, search_keyword: search }, // backend jo param padhta ho
        });
        const list = toList(res);
        list.forEach((i) => { this.itemCache[i.id] = i; });
        this.items = list;
      } catch (error) {
        this.$swalToast(this.firstError(error), 'error');
      } finally {
        this.itemsLoading = false;
      }
    },

    onItemSearch(search) {
      clearTimeout(this.itemSearchTimer);
      if (!search || search.trim().length < 2) {
        this.items = [];
        return;
      }
      this.itemSearchTimer = setTimeout(() => this.fetchItems(search.trim()), 400);
    },

    // edit ke time jo items default list mein nahi hain unka label laao
    async ensureItems(ids) {
      const missing = [...new Set(ids.filter(Boolean))].filter((id) => !this.itemCache[id]);
      await Promise.all(
        missing.map(async (id) => {
          try {
            const res = await axios.get(`/items/${id}`);
            const it = res?.data?.data;
            if (it?.id) this.itemCache[it.id] = it;
          } catch (e) { /* label nahi mila to id dikhegi, save phir bhi chalega */ }
        }),
      );
    },

    // ---------- create / edit ----------
    openCreate() {
      this.form = blankForm();
      this.errors = {};
      this.showModal('item_mapping_modal');
    },

    async openEdit(row) {
      this.errors = {};
      this.setLoading(true);
      try {
        const response = await axios.get(`/item-mappings/${row.id}`);
        const d = response.data.data;
        this.form = {
          id: d.id,
          name: d.name || '',
          main_item_id: d.main_item_id || '',
          lines: (d.lines || []).map((l) => ({
            id: l.id ?? null,
            type: l.child_item_id ? 'item' : 'group',
            child_item_group_id: l.child_item_group_id || '',
            child_item_id: l.child_item_id || '',
            qty_multiplier: l.qty_multiplier ?? 1,
            rmt_width_multiplier: l.rmt_width_multiplier ?? '',
            rmt_height_multiplier: l.rmt_height_multiplier ?? '',
            option_set: l.option_set ?? '',
          })),
        };
        if (!this.form.lines.length) this.addLine();
        // response mein relation aaya ho to label wahin se le lo
        [d.main_item, ...(d.lines || []).map((l) => l.child_item)].forEach((it) => {
          if (it?.id) this.itemCache[it.id] = it;
        });
        await this.ensureItems([this.form.main_item_id, ...this.form.lines.map((l) => l.child_item_id)]);
        this.showModal('item_mapping_modal');
      } catch (error) {
        this.$swalToast(this.firstError(error), 'error');
      } finally {
        this.setLoading(false);
      }
    },

    addLine() {
      this.form.lines.push(blankLine());
    },
    removeLine(i) {
      this.form.lines.splice(i, 1);
    },

    buildPayload() {
      return {
        name: this.form.name,
        main_item_id: this.form.main_item_id || null,
        lines: this.form.lines.map((l) => {
          const line = {};
          // existing line ka id bhejna zaroori: warna edit par naye lines ban jaate hain
          // aur purani quotations ki mapping_line_id soft-deleted line par chali jaati hai
          if (l.id) line.id = l.id;
          if (l.type === 'item') line.child_item_id = l.child_item_id || null;
          else line.child_item_group_id = l.child_item_group_id || null;

          line.qty_multiplier = l.qty_multiplier === '' ? 1 : Number(l.qty_multiplier);
          // optional fields: khaali ho to bhejte hi nahi (payload sample jaisa)
          if (l.rmt_width_multiplier !== '' && l.rmt_width_multiplier !== null) {
            line.rmt_width_multiplier = Number(l.rmt_width_multiplier);
          }
          if (l.rmt_height_multiplier !== '' && l.rmt_height_multiplier !== null) {
            line.rmt_height_multiplier = Number(l.rmt_height_multiplier);
          }
          if (l.option_set && l.option_set.trim() !== '') {
            line.option_set = l.option_set.trim();
          }
          return line;
        }),
      };
    },

    async save() {
      this.errors = {};
      this.saving = true;
      try {
        const payload = this.buildPayload();
        const response = this.isEdit
          ? await axios.put(`/item-mappings/${this.form.id}`, payload)
          : await axios.post('/item-mappings', payload);
        this.$swalToast(response.data.message);
        this.hideModal('item_mapping_modal');
        this.itemMappings(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(this.readErrors(error), 'error');
      } finally {
        this.saving = false;
      }
    },

    // ---------- clone ----------
    openClone(row) {
      this.cloneRow = row;
      this.clone_main_item_id = '';
      this.errors = {};
      this.showModal('item_mapping_clone_modal');
    },

    async submitClone() {
      if (!this.clone_main_item_id) {
        this.$swalToast('Please select main item', 'warning');
        return;
      }
      this.saving = true;
      try {
        const response = await axios.post(`/item-mappings/${this.cloneRow.id}/clone`, {
          main_item_id: this.clone_main_item_id,
        });
        this.$swalToast(response.data.message);
        this.hideModal('item_mapping_clone_modal');
        this.itemMappings();
      } catch (error) {
        this.$swalToast(this.readErrors(error), 'error');
      } finally {
        this.saving = false;
      }
    },

    // ---------- resolve ----------
    openResolve() {
      this.resolveForm = { item_id: '', width: '', height: '', qty: '' };
      this.resolved = false;
      this.resolveResult = null;
      this.resolveError = null;
      this.errors = {};
      this.showModal('item_mapping_resolve_modal');
    },

    async submitResolve() {
      this.errors = {};
      this.resolved = false;
      this.resolveResult = null;
      this.resolveError = null;
      this.saving = true;
      try {
        const query_string = new URLSearchParams(this.resolveForm).toString();
        const response = await axios.get(`/item-mappings/resolve?${query_string}`);
        // null => is item ka mapping hi nahi hai, kuch prefill nahi karna
        this.resolveResult = response.data.data ?? null;
        this.resolved = true;
      } catch (error) {
        this.resolveError = this.readErrors(error);
      } finally {
        this.saving = false;
      }
    },

    // ---------- delete ----------
    async deleteMappings(ids) {
      if (!ids.length) {
        this.$swalToast('Please select at least one option.', 'warning');
        return;
      }
      const result = await this.$confirm({});
      if (!result.isConfirmed) return;

      this.setLoading(true);
      try {
        // backend mein delete ki id URL mein jaati hai: DELETE /item-mappings/{id}
        // bulk select ho to har id ke liye alag call
        const results = await Promise.allSettled(ids.map((id) => axios.delete(`/item-mappings/${id}`)));
        const failed = results.filter((r) => r.status === 'rejected');
        const okCount = results.length - failed.length;
        if (failed.length) {
          this.$swalToast(
            (okCount ? `${okCount} deleted, ${failed.length} failed: ` : '') + this.firstError(failed[0].reason),
            'error',
          );
        } else {
          this.$swalToast(results[0].value?.data?.message || 'Deleted successfully');
        }
      } finally {
        this.itemMappings(); // success/fail dono mein list refresh (stale rows hat jaayengi)
        this.setLoading(false);
      }
    },
  },
};
</script>

<style scoped>
.qm-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f1f5f9; }
.qm-topbar {
  background: #fff; border-bottom: 1px solid #e2e8f0; padding: 12px 22px;
  display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
}
.qm-page-title { font-size: 14px; font-weight: 700; color: #0f172a; }
.qm-page-sub { font-size: 11px; color: #94a3b8; }
.qm-close-btn {
  width: 30px; height: 30px; border-radius: 7px; background: #f1f5f9; border: 1px solid #ef4444;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  font-size: 13px; transition: all .15s; color: #ef4444;
}
.qm-close-btn:hover { background: #fee2e2; border-color: #fca5a5; color: #ef4444; }
.qm-body { flex: 1; overflow-y: auto; padding: 18px 22px; scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
.qm-body::-webkit-scrollbar { width: 4px; }
.qm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.qm-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 14px; overflow: visible; }
.qm-card-head { display: flex; align-items: center; gap: 9px; padding: 10px 16px; background: #f8fafc; border-bottom: 1px solid #e8edf3; }
.qm-card-icon { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; }
.qm-card-title { font-size: 12px; font-weight: 700; color: #0f172a; letter-spacing: .1px; }
.qm-card-body { padding: 14px 16px; }
.qm-label { font-size: 11px; font-weight: 600; color: #475569; margin-bottom: 4px; display: block; }
.qm-input {
  width: 100%; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 0 11px; font-size: 12.5px; color: #1e293b;
  background: #fff; outline: none; height: 35px; transition: border-color .15s, box-shadow .15s;
}
.qm-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }
.qm-input.err { border-color: #f43f5e; box-shadow: 0 0 0 3px rgba(244,63,94,.1); }
.qm-err-msg { font-size: 10.5px; color: #f43f5e; margin-top: 3px; display: flex; align-items: center; gap: 3px; }
.qm-item-card {
  background: #fff; border: 1.5px solid #e8edf3; border-radius: 10px; padding: 14px 14px 10px;
  margin-bottom: 10px; position: relative; transition: border-color .15s, box-shadow .15s;
}
.qm-item-card:hover { border-color: #bfdbfe; box-shadow: 0 2px 10px rgba(59,130,246,.07); }
.qm-item-num {
  position: absolute; top: -9px; left: 14px; width: 19px; height: 19px; background: #3b82f6; color: #fff;
  border-radius: 50%; font-size: 9px; font-weight: 700; display: flex; align-items: center; justify-content: center;
}
.qm-remove-btn {
  width: 30px; height: 30px; border-radius: 7px; background: #fff0f3; border: 1.5px solid #fecdd3; color: #f43f5e;
  font-size: 13px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s;
}
.qm-remove-btn:hover { background: #f43f5e; border-color: #f43f5e; color: #fff; }
.qm-remove-btn:disabled { opacity: .3; cursor: not-allowed; }
.qm-add-item-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; background: #eff6ff;
  border: 1.5px dashed #93c5fd; border-radius: 8px; color: #2563eb; font-size: 11.5px; font-weight: 600;
  cursor: pointer; transition: all .15s;
}
.qm-add-item-btn:hover { background: #dbeafe; border-color: #3b82f6; }
.qm-footer {
  background: #fff; border-top: 1px solid #e2e8f0; padding: 11px 22px;
  display: flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap;
}
.qm-btn {
  display: inline-flex; align-items: center; gap: 5px; padding: 7px 15px; border-radius: 8px; font-size: 12px;
  font-weight: 600; cursor: pointer; border: 1.5px solid transparent; transition: all .15s; text-decoration: none; white-space: nowrap;
}
.qm-btn-primary { background: linear-gradient(135deg,#2563eb,#3b82f6); color: #fff; border-color: #2563eb; box-shadow: 0 2px 8px rgba(37,99,235,.25); }
.qm-btn-primary:hover { background: linear-gradient(135deg,#1d4ed8,#2563eb); }
.qm-btn-ghost { background: #f8fafc; color: #475569; border-color: #e2e8f0; }
.qm-btn-ghost:hover { background: #f1f5f9; }
.qm-btn:disabled { opacity: .5; cursor: not-allowed; }
:deep(.vs__dropdown-toggle) {
  border: 1.5px solid #e2e8f0 !important; border-radius: 8px !important; min-height: 35px !important; padding: 0 8px !important;
  transition: border-color .15s, box-shadow .15s !important;
}
:deep(.vs__dropdown-toggle:focus-within) { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,.1) !important; }
:deep(.vs__search) { font-size: 12.5px !important; color: #1e293b !important; }
:deep(.vs__selected) {
  font-size: 12px !important; color: #1e293b !important; background: #eff6ff !important;
  border: 1px solid #bfdbfe !important; border-radius: 5px !important;
}
</style>

<template>
<layout-header></layout-header>
<layout-sidebar></layout-sidebar>

<div class="page-wrapper">
<div class="content">

  <!-- Breadcrumb -->
  <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
    <breadcrumb :title="title" :text="text" :text1="text1" />
    <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
      <div class="me-2 mb-2">
        <button type="button" class="btn btn-outline-secondary" @click="openResolve()">
          <i class="ti ti-search me-1"></i> Resolve
        </button>
      </div>
      <div class="me-2 mb-2">
        <button type="button" class="btn btn-outline-danger" @click="deleteMappings(selected_rows)">
          <i class="ti ti-trash me-1"></i> Delete Selected
        </button>
      </div>
      <div class="mb-2">
        <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openCreate()">
          <i class="ti ti-circle-plus me-2"></i>Add Item Mapping
        </a>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
      <h5>Item Mapping List</h5>
      <div class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
        <div class="me-3" v-if="search_keyword !== ''">
          <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
        </div>
        <div class="me-3">
          <input type="text" class="form-control" v-model="search_keyword" placeholder="Search...." @keyup.enter="itemMappings()" />
        </div>
        <div class="me-3">
          <button type="button" class="btn btn-dark" @click="itemMappings()"><i class="ti ti-search"></i></button>
        </div>
      </div>
    </div>

    <div class="card-body card-table-wrapper">
      <app-table
        :columns="tableColumns"
        :rows="data"
        :pagination="pagination"
        :loading="false"
        empty-text="No records found."
        empty-icon="ti ti-file-off"
        @page-change="itemMappings"
      >
        <template #header-checkbox>
          <div class="form-check form-checkbox-success mb-2">
            <input type="checkbox" class="form-check-input" :checked="allSelected" @change="handleToggleAll">
          </div>
        </template>

        <template #checkbox="{ row }">
          <div class="form-check form-checkbox-success mb-2">
            <input type="checkbox" class="form-check-input" :value="row.id" v-model="selected_rows">
          </div>
        </template>

        <template #name="{ row }">
          <a href="javascript:void(0);" class="text-primary" @click="openEdit(row)">{{ row.name }}</a>
        </template>

        <template #main_item="{ row }">
          {{ row.main_item?.name || '-' }}
        </template>

        <template #lines_count="{ row }">
          {{ row.lines_count ?? row.lines?.length ?? '-' }}
        </template>

        <template #created_at="{ row }">
          {{ $formatDate(row.created_at) }}
        </template>

        <template #actions="{ row }">
          <div class="dropdown">
            <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <i class="ti ti-dots-vertical"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openEdit(row)">
                  <i class="ti ti-edit me-1"></i>Edit
                </a>
              </li>
              <li>
                <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openClone(row)">
                  <i class="ti ti-copy me-1"></i>Clone
                </a>
              </li>
              <li>
                <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="deleteMappings([row.id])">
                  <i class="ti ti-trash me-1"></i>Delete
                </a>
              </li>
            </ul>
          </div>
        </template>
      </app-table>
    </div>
  </div>
</div>

<app-footer></app-footer>
</div>

<!-- ============ CREATE / EDIT MODAL (fullscreen) ============ -->
<div class="modal fade" id="item_mapping_modal" tabindex="-1" aria-hidden="true">
  <div class="modal-dialog modal-fullscreen" style="margin:0;">
    <div class="modal-content border-0 h-100" style="border-radius:0;">

      <div class="qm-main">

        <!-- Topbar -->
        <div class="qm-topbar">
          <div>
            <div class="qm-page-title">{{ isEdit ? 'Edit Item Mapping' : 'Add Item Mapping' }}</div>
            <div class="qm-page-sub">{{ isEdit ? 'Update mapping details below' : 'Fill in the details to create a new item mapping' }}</div>
          </div>
          <button type="button" class="qm-close-btn" data-bs-dismiss="modal">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="save" class="d-flex flex-column" style="flex:1;overflow:hidden;">
          <div class="qm-body">

            <div v-if="errorsOther.length" class="alert alert-danger py-2">
              <div v-for="(msg, k) in errorsOther" :key="k">{{ msg }}</div>
            </div>

            <!-- 1. Mapping Details -->
            <div class="qm-card">
              <div class="qm-card-head">
                <div class="qm-card-icon" style="background:#eff6ff;"><i class="ti ti-hierarchy-2" style="color:#3b82f6;"></i></div>
                <span class="qm-card-title">Mapping Details</span>
              </div>
              <div class="qm-card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="qm-label">Name <span style="color:#f43f5e;">*</span></label>
                    <input type="text" class="qm-input" :class="{ 'err': fieldError('name') }" v-model="form.name" placeholder="Enter mapping name" />
                    <div v-if="fieldError('name')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ fieldError('name') }}</div>
                  </div>
                  <div class="col-md-6">
                    <label class="qm-label">Main Item <span style="color:#f43f5e;">*</span></label>
                    <vue-select
                      v-model="form.main_item_id"
                      :options="itemOptions" :filterable="false" :loading="itemsLoading" @search="onItemSearch"
                      label="label"
                      :reduce="(o) => o.id"
                      placeholder="Select item"
                    >
                      <template #no-options="{ search }">{{ search && search.length >= 2 ? (itemsLoading ? 'Searching...' : 'No items found') : 'Type 2+ letters to search item' }}</template>
                    </vue-select>
                    <div v-if="fieldError('main_item_id')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ fieldError('main_item_id') }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2. Mapping Lines -->
            <div class="qm-card">
              <div class="qm-card-head">
                <div class="qm-card-icon" style="background:#fffbeb;"><i class="ti ti-list-details" style="color:#f59e0b;"></i></div>
                <span class="qm-card-title">Mapping Lines</span>
                <button type="button" class="qm-add-item-btn ms-auto" @click="addLine()">
                  <i class="ti ti-plus"></i> Add Line
                </button>
              </div>
              <div class="qm-card-body">

                <div v-for="(line, i) in form.lines" :key="i" class="qm-item-card">
                  <div class="qm-item-num">{{ i + 1 }}</div>
                  <div class="row g-2 align-items-end">

                    <div class="col-md-2">
                      <label class="qm-label">Type</label>
                      <vue-select
                        v-model="line.type"
                        :options="lineTypeOptions"
                        label="label"
                        :reduce="(o) => o.id"
                        :clearable="false"
                        :searchable="false"
                      />
                    </div>

                    <div class="col-md-3">
                      <label class="qm-label">{{ line.type === 'item' ? 'Item' : 'Category' }} <span style="color:#f43f5e;">*</span></label>
                      <vue-select
                        v-if="line.type === 'item'"
                        v-model="line.child_item_id"
                        :options="itemOptions" :filterable="false" :loading="itemsLoading" @search="onItemSearch"
                        label="label"
                        :reduce="(o) => o.id"
                        placeholder="Select item"
                      >
                        <template #no-options="{ search }">{{ search && search.length >= 2 ? (itemsLoading ? 'Searching...' : 'No items found') : 'Type 2+ letters to search item' }}</template>
                      </vue-select>
                      <vue-select
                        v-else
                        v-model="line.child_item_group_id"
                        :options="groupOptions"
                        label="label"
                        :reduce="(o) => o.id"
                        placeholder="Select category"
                      />
                      <div v-if="fieldError(`lines.${i}.child_item_group_id`) || fieldError(`lines.${i}.child_item_id`)" class="qm-err-msg">
                        <i class="ti ti-alert-circle"></i>{{ fieldError(`lines.${i}.child_item_group_id`) || fieldError(`lines.${i}.child_item_id`) }}
                      </div>
                    </div>

                    <div class="col-md-1">
                      <label class="qm-label" title="Qty Multiplier">Qty Mult. <span style="color:#f43f5e;">*</span></label>
                      <input type="number" step="any" min="0" class="qm-input" :class="{ 'err': lineError(i, 'qty_multiplier') }" v-model="line.qty_multiplier" />
                      <div v-if="lineError(i, 'qty_multiplier')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ lineError(i, 'qty_multiplier') }}</div>
                    </div>

                    <div class="col-md-2">
                      <label class="qm-label">Width Multiplier</label>
                      <input type="number" step="any" min="0" class="qm-input" :class="{ 'err': lineError(i, 'rmt_width_multiplier') }" v-model="line.rmt_width_multiplier" placeholder="Optional" />
                      <div v-if="lineError(i, 'rmt_width_multiplier')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ lineError(i, 'rmt_width_multiplier') }}</div>
                    </div>

                    <div class="col-md-2">
                      <label class="qm-label">Height Multiplier</label>
                      <input type="number" step="any" min="0" class="qm-input" :class="{ 'err': lineError(i, 'rmt_height_multiplier') }" v-model="line.rmt_height_multiplier" placeholder="Optional" />
                      <div v-if="lineError(i, 'rmt_height_multiplier')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ lineError(i, 'rmt_height_multiplier') }}</div>
                    </div>

                    <div class="col-md-1">
                      <label class="qm-label">Option Set</label>
                      <input type="text" class="qm-input" :class="{ 'err': lineError(i, 'option_set') }" v-model="line.option_set" list="option_set_list" placeholder="Optional" />
                      <div v-if="lineError(i, 'option_set')" class="qm-err-msg"><i class="ti ti-alert-circle"></i>{{ lineError(i, 'option_set') }}</div>
                    </div>

                    <div class="col-md-1 d-flex align-items-end">
                      <button type="button" class="qm-remove-btn" :disabled="form.lines.length === 1" @click="removeLine(i)">
                        <i class="ti ti-trash"></i>
                      </button>
                    </div>

                  </div>
                </div>

                <div v-if="!form.lines.length" style="text-align:center;padding:28px;color:#94a3b8;">
                  <i class="ti ti-inbox" style="font-size:28px;display:block;margin-bottom:6px;opacity:.4;"></i>
                  No lines added yet.
                </div>

                <datalist id="option_set_list">
                  <option v-for="o in optionSetSuggestions" :key="o" :value="o"></option>
                </datalist>
              </div>
            </div>

          </div><!-- /qm-body -->

          <!-- Footer -->
          <div class="qm-footer">
            <button type="button" class="qm-btn qm-btn-ghost me-auto" @click="addLine()">
              <i class="ti ti-plus"></i> Add Line
            </button>
            <button type="button" class="qm-btn qm-btn-ghost" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="qm-btn qm-btn-primary" :disabled="saving">
              <i class="ti ti-device-floppy"></i> {{ isEdit ? 'Update Mapping' : 'Save Mapping' }}
            </button>
          </div>
        </form>
      </div><!-- /qm-main -->

    </div>
  </div>
</div>

<!-- ============ CLONE MODAL ============ -->
<div class="modal fade" id="item_mapping_clone_modal" tabindex="-1">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Clone — {{ cloneRow?.name }}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <div v-if="errorsOther.length" class="alert alert-danger py-2">
          <div v-for="(msg, k) in errorsOther" :key="k">{{ msg }}</div>
        </div>
        <label class="form-label">New Main Item <span class="text-danger">*</span></label>
        <vue-select
          v-model="clone_main_item_id"
          :options="itemOptions" :filterable="false" :loading="itemsLoading" @search="onItemSearch"
          label="label"
          :reduce="(o) => o.id"
          placeholder="Select item"
        >
<template #no-options="{ search }">{{ search && search.length >= 2 ? (itemsLoading ? 'Searching...' : 'No items found') : 'Type 2+ letters to search item' }}</template>
</vue-select>
        <small class="text-danger d-block" v-if="fieldError('main_item_id')">{{ fieldError('main_item_id') }}</small>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="submitClone()">Clone</button>
      </div>
    </div>
  </div>
</div>

<!-- ============ RESOLVE MODAL ============ -->
<div class="modal fade" id="item_mapping_resolve_modal" tabindex="-1">
  <div class="modal-dialog modal-dialog-centered modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Resolve Item Mapping</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Item <span class="text-danger">*</span></label>
            <vue-select
              v-model="resolveForm.item_id"
              :options="itemOptions" :filterable="false" :loading="itemsLoading" @search="onItemSearch"
              label="label"
              :reduce="(o) => o.id"
              placeholder="Select item"
            >
<template #no-options="{ search }">{{ search && search.length >= 2 ? (itemsLoading ? 'Searching...' : 'No items found') : 'Type 2+ letters to search item' }}</template>
</vue-select>
            <small class="text-danger d-block" v-if="fieldError('item_id')">{{ fieldError('item_id') }}</small>
          </div>
          <div class="col-md-2 mb-3">
            <label class="form-label">Width <span class="text-danger">*</span></label>
            <input type="number" class="form-control" :class="{ 'is-invalid': fieldError('width') }" v-model="resolveForm.width" />
            <small class="text-danger d-block" v-if="fieldError('width')">{{ fieldError('width') }}</small>
          </div>
          <div class="col-md-2 mb-3">
            <label class="form-label">Height <span class="text-danger">*</span></label>
            <input type="number" class="form-control" :class="{ 'is-invalid': fieldError('height') }" v-model="resolveForm.height" />
            <small class="text-danger d-block" v-if="fieldError('height')">{{ fieldError('height') }}</small>
          </div>
          <div class="col-md-2 mb-3">
            <label class="form-label">Qty <span class="text-danger">*</span></label>
            <input type="number" min="0" step="any" class="form-control" :class="{ 'is-invalid': fieldError('qty') }" v-model="resolveForm.qty" />
            <small class="text-danger d-block" v-if="fieldError('qty')">{{ fieldError('qty') }}</small>
          </div>
        </div>

        <div v-if="resolveError && !Object.keys(errors).length" class="alert alert-danger py-2">{{ resolveError }}</div>
        <div v-if="resolved && resolveResult === null" class="alert alert-warning py-2">
          Is item ka koi mapping nahi mila — quotation mein kuch prefill nahi hoga.
        </div>
        <div v-if="resolveSummary" class="mb-2 small">
          <span class="badge bg-success me-2">Common rows: {{ resolveSummary.common }}</span>
          <span class="badge bg-info me-1" v-for="n in resolveSummary.sets" :key="n">{{ n }}</span>
          <span v-if="!resolveSummary.sets.length" class="text-muted">No option sets</span>
        </div>
        <pre v-if="resolveResult" class="bg-light p-3 rounded mb-0" style="max-height:300px;overflow:auto;">{{ JSON.stringify(resolveResult, null, 2) }}</pre>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="submitResolve()">Resolve</button>
      </div>
    </div>
  </div>
</div>
</template>